import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  FiArrowRight,
  FiBox,
  FiCalendar,
  FiCheck,
  FiDollarSign,
  FiMapPin,
  FiPackage,
  FiTruck,
} from "react-icons/fi";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { IoDocumentTextOutline } from "react-icons/io5";
import {
  MdOutlineVerified,
  MdOutlineLocalShipping,
  MdOutlineInventory2,
  MdOutlinePhotoLibrary,
} from "react-icons/md";
import { HiOutlineHashtag, HiOutlineSparkles } from "react-icons/hi2";
import { TbRuler, TbWeight, TbFileInvoice, TbFileFilled } from "react-icons/tb";
import { RiMapPin2Line, RiUserStarLine } from "react-icons/ri";
import axios from "../../../Utils/axiosInstance";
import { s3Upload } from "../../../Utils/s3Upload";
import ConfirmActionModal from "./ConfirmActionModal";
import { useAuth } from "../../Context/AuthContext";
import { ImageModal } from "../Orders/OrderDetailCard";
import AppLoader from "../../Common/AppLoader";

import ProformaInvoiceModal from "./ProformaInvoiceModal";
import { generateInvoicePDF } from "../../../Utils/Pdf/generateInvoicePDFWeb";
import SampleApprovalModal from "./SampleApprovalModal";
import RaiseReturnDisputeModal from "./RaiseReturnDisputeModal";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";
import { generateDeclarationPDF } from "../../../Utils/Pdf/Declaration/generateDeclarationPDF";
import { downloadVisitPassPDF } from "../../../Utils/GenerateVisitPassPdf";
import BankTransferUploadSection from "./BankTransferUploadSection";
import SampleBankTransferStatusCard from "./SampleBankTransferStatusCard";
import ReturnDispatchCard from "../Orders/ReturnDispatchCard";
// ─────────────────────────────────────────────────────────────────────────────


/* ── DISPUTE HELPERS (mirrors OrderDetailCard's dispute UI) ─────────────── */
const isDisputeOrder = (stage) =>
  [
    "Dispute_Raised",
    "Dispute_Under_Investigation",
    "Full_Order_Returned",
    "Partial_Order_Returned",
  ].includes(stage);

const getReturnTypeFromStage = (stage) => {
  if (stage === "Full_Order_Returned") return "Full";
  if (stage === "Partial_Order_Returned") return "Partial";
  return null;
};

const DISPUTE_STAGES = [
  { key: "Dispute_Raised", label: "Dispute Raised", icon: "⚠️" },
  { key: "Dispute_Under_Investigation", label: "Under Investigation", icon: "🔍" },
  { key: "Resolved", label: "Resolved", icon: "✅" },
];

const PaymentMethodFlag = ({ method, amount = 0 }) => {
  const razorpayEligible = amount < 50000;

  const methods = {
    "Online": {
      label: "🚀 Instant",
      color: "bg-green-50 border-green-200 text-green-700",
      description: "Immediate payment processing",
      show: razorpayEligible
    },
    "Letter_Of_Credit": {
      label: "🐢 Slower",
      color: "bg-blue-50 border-blue-200 text-blue-700",
      description: "Requires verification (2-3 days)",
      show: true
    },
    "Direct_Bank_Transfer": {
      label: "⏱️ Medium",
      color: "bg-orange-50 border-orange-200 text-orange-700",
      description: "Requires verification (24-48 hrs)",
      show: true
    }
  };

  const config = methods[method];
  if (!config || !config.show) return null;

  return (
    <div className={`border rounded-lg px-3 py-2 ${config.color}`}>
      <p className="text-xs font-bold">{config.label}</p>
      <p className="text-[10px] opacity-80 mt-0.5">{config.description}</p>
    </div>
  );
};

const DisputeStageTimeline = ({ currentStage }) => {
  const activeIdx = DISPUTE_STAGES.findIndex((s) => s.key === currentStage);
  const isResolved =
    currentStage === "Full_Order_Returned" ||
    currentStage === "Partial_Order_Returned";

  const displayStages = DISPUTE_STAGES.map((stage, idx) => {
    if (idx === 2 && isResolved) {
      const returnType = getReturnTypeFromStage(currentStage);
      if (returnType) {
        return { key: currentStage, label: `${returnType} Order Returned`, icon: "📦" };
      }
    }
    return stage;
  });

  return (
    <div className="space-y-0">
      {displayStages.map((stage, idx) => {
        const isDone = idx < activeIdx;
        const isCurrent = idx === activeIdx;
        const isLast = idx === displayStages.length - 1;
        return (
          <div key={stage.key} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <div className="relative">
                {isCurrent && (
                  <span className="absolute inset-0 rounded-full animate-ping bg-amber-500/20 scale-150" />
                )}
                <div
                  className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center border-2 z-10 transition-all duration-500
                    ${isDone
                      ? "bg-amber-500 border-amber-500 shadow-md shadow-amber-300/30"
                      : isCurrent
                        ? "bg-amber-500 border-amber-500 text-white shadow-lg shadow-amber-300/40 scale-110"
                        : "bg-white border-gray-200"
                    }`}
                >
                  {isDone ? (
                    <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className={`text-sm ${isCurrent ? "text-white animate-bounce" : ""}`}>
                      {stage.icon}
                    </span>
                  )}
                </div>
              </div>
              {!isLast && (
                <div
                  className={`w-0.5 flex-1 min-h-[32px] my-0.5 rounded-full transition-all duration-700
                    ${isDone
                      ? "bg-gradient-to-b from-amber-500 to-amber-300"
                      : isCurrent
                        ? "bg-gradient-to-b from-amber-500 to-gray-200"
                        : "bg-gray-200"
                    }`}
                />
              )}
            </div>
            <div className="pb-4 pt-1.5">
              <p
                className={`text-sm font-semibold transition-all duration-300
                  ${isCurrent ? "text-amber-600 scale-105" : isDone ? "text-gray-700" : "text-gray-400"}`}
              >
                {stage.label}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DisputeAlertBanner = ({ currentStage }) => {
  const isInvestigation = currentStage === "Dispute_Under_Investigation";
  const isResolved =
    currentStage === "Full_Order_Returned" || currentStage === "Partial_Order_Returned";

  return (
    <>
      <style>{`
        @keyframes shimmerQV { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
        @keyframes warningPulseQV { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.05); } }
      `}</style>
      <div className="relative overflow-hidden rounded-2xl border border-amber-300 bg-white p-5">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg,transparent,transparent 10px,currentColor 10px,currentColor 11px)",
            color: "#f59e0b",
          }}
        />
        <div className="relative flex items-center justify-between mb-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-amber-500 flex items-center gap-2">
            <span className="inline-block w-3 h-0.5 bg-amber-500 rounded-full" />
            Dispute Status
            <span className="inline-block w-3 h-0.5 bg-amber-500 rounded-full" />
          </p>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[12px] font-bold text-amber-600">
              {isInvestigation ? "Under Investigation" : isResolved ? "Resolved" : "Dispute Raised"}
            </span>
          </div>
        </div>

        <div className="relative flex items-center justify-center gap-4 py-2">
          <div className="relative flex-shrink-0">
            <div className="absolute w-16 h-16 inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-amber-400/30 animate-ping" />
            </div>
            <div
              className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center text-3xl border-3 bg-amber-50 border-amber-400 shadow-lg shadow-amber-300/50"
              style={{ animation: "warningPulseQV 2s ease-in-out infinite" }}
            >
              ⚠️
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-amber-600 mb-1">
              {isInvestigation ? "Dispute Under Investigation" : isResolved ? "Dispute Resolved" : "This Order Has an Active Dispute"}
            </h3>
            <p className="text-xs text-amber-700 leading-relaxed">
              {isInvestigation
                ? "Our Order Fulfillment Manager (OFM) is reviewing your dispute. You'll be notified once the investigation is complete."
                : isResolved
                  ? `Dispute has been resolved. Order marked as ${getReturnTypeFromStage(currentStage) ? "returned" : "completed"}.`
                  : "A dispute has been raised on this order. Our team is reviewing it. You will be notified once a resolution is reached."}
            </p>
          </div>
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            <div
              className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-amber-200/20 to-transparent"
              style={{ animation: "shimmerQV 3s infinite" }}
            />
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-amber-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[11px] text-amber-600 font-semibold">
              {isInvestigation ? "Verification in Progress" : isResolved ? "Ready for Completion" : "Awaiting Resolution"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-200">
            <span className="text-[10px] text-amber-600">
              {isInvestigation ? "🔍 OFM Reviewing" : isResolved ? "✓ Complete" : "⏱️ Under Review"}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

const OfmAvatar = ({ photo, name, size = "w-11 h-11", textSize = "text-xs" }) => {
  const [imgError, setImgError] = useState(false);
  const initials = (name || "OFM")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join("");

  if (photo && !imgError) {
    return (
      <img
        src={photo}
        alt={name || "OFM"}
        onError={() => setImgError(true)}
        className={`${size} rounded-full object-cover border border-gray-200 shadow-sm flex-shrink-0`}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold flex items-center justify-center shadow-sm flex-shrink-0 ${textSize}`}
    >
      {initials || "OFM"}
    </div>
  );
};

const ClientVisitPassCard = ({ pass }) => {
  const [downloading, setDownloading] = useState(false);
  if (!pass) return null;

  const isDispute = pass.passType === "Dispute";
  const isSample = pass.passType === "Sample";

  const ofmPhoto =
    pass.ofmDetails?.photo ||
    pass.ofmId?.profilePic ||
    pass.ofmId?.identityVerification?.aadhaar?.photoUrl ||
    null;

  const ofmName =
    pass.ofmDetails?.name ||
    (pass.ofmId
      ? `${pass.ofmId.firstName || ""} ${pass.ofmId.lastName || ""}`.trim()
      : "OFM Representative");

  const stageBadgeText = isSample
    ? "📦 Sample Stage Pass"
    : isDispute
      ? "⚠️ Dispute Resolution Pass"
      : "🚚 Full Order Pass";

  const stageBadgeBg = isSample
    ? "bg-blue-100 text-blue-800 border-blue-300"
    : isDispute
      ? "bg-amber-100 text-amber-900 border-amber-300 font-black"
      : "bg-emerald-100 text-emerald-800 border-emerald-300";

  const cardBorder = isDispute
    ? "border-amber-300 bg-gradient-to-br from-amber-50/90 via-white to-orange-50/60"
    : isSample
      ? "border-blue-200 bg-gradient-to-br from-blue-50/70 via-white to-indigo-50/40"
      : "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50";

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await downloadVisitPassPDF(pass);
      toast.success("Visit Pass PDF downloaded successfully");
    } catch (err) {
      console.error("Failed to download visit pass PDF:", err);
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`rounded-2xl border ${cardBorder} p-5 shadow-sm space-y-4`}>
      <div className="flex items-center justify-between border-b border-gray-100 pb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl ${isDispute ? "bg-amber-500" : "bg-emerald-600"} text-white flex items-center justify-center font-bold text-base shadow-sm`}>
            {isDispute ? "⚠️" : "📇"}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-bold text-gray-900">
                {isDispute ? "Dispute Resolution Visit Pass" : "OFM Client Visit Pass"}
              </h4>
              <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold uppercase border ${stageBadgeBg}`}>
                {stageBadgeText}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 font-medium">
              {isDispute
                ? "Official Dispute Investigation Pass • Assigned to visit Buyer Side"
                : "Official Verification Pass Issued by PCM Exports Admin"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-800 text-[11px] font-mono font-bold tracking-wider uppercase">
            {pass.passNumber || "ACTIVE PASS"}
          </span>
          <button
            type="button"
            disabled={downloading}
            onClick={handleDownload}
            className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg text-white shadow-sm transition-all ${isDispute ? "bg-amber-600 hover:bg-amber-700" : "bg-primaryColor hover:bg-primaryColor/90"
              }`}
          >
            <span>{downloading ? "Preparing..." : "📥 Download Pass PDF"}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm flex items-center gap-3">
          <OfmAvatar photo={ofmPhoto} name={ofmName} size="w-12 h-12" />
          <div className="space-y-0.5 min-w-0">
            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Assigned OFM Agent</p>
            <p className="text-sm font-extrabold text-gray-900 truncate">{ofmName}</p>
            <p className="text-xs text-gray-600">📞 Contact: <span className="font-semibold text-gray-800">{pass.ofmDetails?.phone || pass.ofmId?.phone || "N/A"}</span></p>
            {(pass.ofmDetails?.aadharNumber || pass.ofmId?.aadhaarNumber) && (
              <p className="text-[11px] text-gray-400">Govt ID: ****{(pass.ofmDetails?.aadharNumber || pass.ofmId?.aadhaarNumber).slice(-4)}</p>
            )}
          </div>
        </div>
        {/* <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm space-y-1">
          <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Purpose & Destination</p>
          <p className="text-xs font-bold text-gray-800">{pass.assignmentDetails || "Quality & Spec Verification"}</p>
          <p className="text-[11px] text-emerald-700 font-semibold">
            {isDispute ? "📍 Inspection Location: Buyer Warehouse / Delivery Site " : "📍 Destination: Verified Client Location"}
          </p>
          <p className="text-[10px] text-gray-400 font-mono">Assignment #{pass.assignmentNumber || "N/A"}</p>
        </div> */}
      </div>

      <div className="pt-2.5 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600">
        <span>🏢 Client Address: <strong className="text-gray-800">{pass.clientDetails?.address || "Warehouse / Office"}</strong></span>
        <span>Valid Until: <strong className={isDispute ? "text-amber-700 font-bold" : "text-emerald-700 font-bold"}>{pass.validity?.toDate ? new Date(pass.validity.toDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</strong></span>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   DESIGN ATOMS  (all original — untouched)
───────────────────────────────────────────────────────────── */

const Block = ({
  label,
  icon: Icon,
  children,
  accent = false,
  className = "",
}) => (
  <div
    className={`rounded-2xl overflow-hidden border ${accent
      ? "border-primaryColor/20 bg-gradient-to-br from-primaryColor/5 via-white to-primaryColor/3"
      : "border-gray-100 bg-white"
      } shadow-sm ${className}`}
  >
    {label && (
      <div
        className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 border-b ${accent
          ? "border-primaryColor/15 bg-primaryColor/5"
          : "border-gray-100 bg-gray-50/80"
          }`}
      >
        {Icon && (
          <span
            className={`flex items-center justify-center w-5 h-5 rounded-md ${accent
              ? "bg-primaryColor text-white"
              : "bg-gray-200 text-gray-500"
              }`}
          >
            <Icon size={11} />
          </span>
        )}
        <span
          className={`text-[10px] font-black uppercase tracking-[0.12em] ${accent ? "text-primaryColor" : "text-gray-400"
            }`}
        >
          {label}
        </span>
      </div>
    )}
    <div className="px-4 sm:px-5 py-4">{children}</div>
  </div>
);

const KV = ({ label, value, valueClass = "" }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-gray-50 last:border-0">
    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0 min-w-[120px] pt-0.5">
      {label}
    </span>
    <span
      className={`text-sm font-semibold text-right break-words ${valueClass || "text-gray-800"}`}
    >
      {value ?? <span className="text-gray-300 font-normal">—</span>}
    </span>
  </div>
);

const Pill = ({ label, color = "gray" }) => {
  const map = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    yellow: "bg-amber-50 text-amber-700 border-amber-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    gray: "bg-gray-100 text-gray-500 border-gray-200",
    primary: "bg-primaryColor/10 text-primaryColor border-primaryColor/25",
  };
  const dotMap = {
    green: "bg-emerald-500",
    red: "bg-red-500",
    yellow: "bg-amber-400 animate-pulse",
    blue: "bg-blue-500",
    gray: "bg-gray-400",
    primary: "bg-primaryColor",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${map[color]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotMap[color]}`} />
      {label}
    </span>
  );
};

const MetricTile = ({
  label,
  value,
  unit,
  sub,
  color = "gray",
  icon: Icon,
}) => {
  const colors = {
    gray: "bg-gray-50 border-gray-100 text-gray-800",
    indigo: "bg-indigo-50 border-indigo-100 text-indigo-800",
    blue: "bg-blue-50 border-blue-100 text-blue-800",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-800",
    primary: "bg-primaryColor/8 border-primaryColor/15 text-primaryColor",
    orange: "bg-orange-50 border-orange-100 text-orange-800",
  };
  return (
    <div
      className={`rounded-xl border p-3.5 flex flex-col gap-1.5 ${colors[color]}`}
    >
      {Icon && (
        <span className="text-current opacity-40">
          <Icon size={14} />
        </span>
      )}
      <p className="text-[10px] font-black uppercase tracking-widest opacity-50">
        {label}
      </p>
      <p className="text-xl font-extrabold leading-none">
        {value ?? <span className="text-gray-300">—</span>}
        {unit && (
          <span className="text-xs font-semibold ml-1 opacity-60">{unit}</span>
        )}
      </p>
      {sub && <p className="text-[11px] opacity-50 font-medium">{sub}</p>}
    </div>
  );
};

const getTrackingSteps = (status) => [
  {
    label: "Dispatched",
    completed: ["Dispatched", "Delivered"].includes(status),
  },
  {
    label: "Out for Delivery",
    completed: ["Dispatched", "Delivered"].includes(status),
  },
  { label: "Delivered", completed: status === "Delivered" },
];


// ── LC Upload Section (shown after LC order is confirmed) ─────────────────
export const LCUploadSection = ({
  quotation,
  lcFile,
  lcDocument,
  setLcFile,
  lcUploading,
  setLcUploading,
  lcStatus,
  isLCVerified,
  isLCUploaded,
  isLCRejected,
  refreshQuotations,
}) => {
  if (isLCVerified) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-emerald-600 text-lg">✅</span>
          <p className="text-sm font-bold text-emerald-700">
            LC Document Verified
          </p>
        </div>

        <p className="text-xs text-emerald-600 leading-relaxed">
          Your Letter of Credit has been verified by our team. Your order is now being processed.
        </p>

        {/* NEW */}
        <div className="mt-3 flex items-center gap-2 bg-emerald-100 rounded-xl px-3 py-2">
          <FiCheck className="text-emerald-700" size={14} />
          <span className="text-xs font-bold text-emerald-700">
            Order Confirmed
          </span>
        </div>
      </div>
    );
  }

  if (isLCUploaded && !isLCRejected) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-blue-600 text-lg">📋</span>
          <p className="text-sm font-bold text-blue-700">LC Document Under Review</p>
        </div>
        <p className="text-xs text-blue-600 leading-relaxed">
          Your LC document has been submitted and is currently under review by our team.
          We will notify you once it is verified.
        </p>
        <div className="mt-3 flex items-center gap-2 bg-blue-100 rounded-xl px-3 py-2">
          <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
          <span className="text-xs font-semibold text-blue-700">Awaiting admin verification</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-blue-700 text-base">💼</span>
          <p className="text-sm font-bold text-blue-700">Upload Letter of Credit</p>
        </div>
        {isLCRejected && (
          <div className="mb-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
            <p className="text-xs font-bold text-red-700">
              ❌ Your previous LC was rejected. Please upload a new document.
            </p>
          </div>
        )}
        <p className="text-xs text-blue-600 leading-relaxed">
          Your order has been reserved. Please upload your Letter of Credit (LC)
          document (PDF or image) to complete the process. Our team will verify it and
          proceed with your order.
        </p>
      </div>

      {/* File Input */}
      <label className="block">
        <span className="text-xs font-semibold text-gray-600 mb-1 block">
          Upload LC Document (PDF, JPG, PNG, WEBP, DOC, DOCX - max 25 MB)
        </span>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.doc,.docx,.xls,.xlsx"
          disabled={lcUploading}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (file.size > 25 * 1024 * 1024) {
              toast.error("File size must be less than 25 MB.");
              return;
            }

            const allowedTypes = [
              "application/pdf",
              "image/jpeg",
              "image/jpg",
              "image/png",
              "image/webp",
              "image/heic",
              "image/heif",
              "image/gif",
              "image/bmp",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ];

            const fileExt = file.name.split(".").pop().toLowerCase();
            const allowedExts = [
              "pdf", "jpg", "jpeg", "png", "webp", "heic", "heif", "gif", "bmp", "doc", "docx", "xls", "xlsx"
            ];

            if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
              toast.error("Invalid file format. Please upload a valid document or image.");
              return;
            }
            setLcUploading(true);
            try {
              const { url } = await s3Upload(file, "lc/buyer");

              setLcFile({
                url,
                name: file.name,
                type: fileExt || "pdf",
                sizeKb: Math.round(file.size / 1024),
              });
              toast.success("LC document ready. Click submit to proceed.");
            } catch {
              toast.error("Upload failed. Please try again.");
            } finally {
              setLcUploading(false);
            }
          }}
          className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-primaryColor/10 file:text-primaryColor hover:file:bg-primaryColor/20 transition"
        />
      </label>

      {lcFile && (
        <p className="text-xs text-green-600 font-semibold">
          ✓ {lcFile.name} ready to submit
        </p>
      )}

      {/* Submit Button */}
      <button
        disabled={!lcFile || lcUploading}
        onClick={async () => {
          if (!lcFile) return;
          setLcUploading(true);
          try {
            if (isLCRejected && lcDocument?._id) {
              await axios.post(`/lc/update-lc/${lcDocument._id}`, {
                file: lcFile,
              });
            } else {
              await axios.post("/lc/upload-lc", {
                orderId: quotation.orderId?._id || quotation.orderId,
                selectedQuotationId: quotation._id,
                file: lcFile,
              });
            }
            toast.success("LC submitted successfully! Our team will verify shortly.");
            setLcFile(null);
            await refreshQuotations?.();
          } catch (err) {
            toast.error(err?.response?.data?.message || "LC submission failed.");
          } finally {
            setLcUploading(false);
          }
        }}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-blue-600 hover:opacity-90 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg"
      >
        {lcUploading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Processing...
          </>
        ) : isLCRejected ? (
          "Re-upload LC Document"
        ) : (
          "Submit LC Document"
        )}
      </button>
    </div>
  );
};

const CountdownTimer = ({ timeRemaining, isExpired }) => {
  const { hours, minutes, seconds } = timeRemaining || {};

  if (isExpired) {
    return (
      <div className="w-full rounded-xl bg-gradient-to-r from-red-50 to-red-100/80 border border-red-200 p-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-1.5">
          <span className="text-xl">⏰</span>
          <p className="text-sm font-bold text-red-700">Decision Period Expired</p>
        </div>
        <p className="text-xs text-red-600">
          The 72-hour window for confirming satisfaction or raising a dispute has passed.
        </p>
      </div>
    );
  }

  const isUrgent = (hours || 0) < 24;
  const isCritical = (hours || 0) < 1;

  return (
    <div
      className={`w-full rounded-xl border p-4 transition-all duration-300 ${isCritical
        ? "bg-red-50/70 border-red-300"
        : isUrgent
          ? "bg-amber-50/70 border-amber-300"
          : "bg-blue-50/60 border-blue-200"
        }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-white shadow-xs border border-gray-100 flex items-center justify-center flex-shrink-0 text-base">
            ⏱️
          </div>
          <div>
            <p
              className={`text-xs font-black uppercase tracking-wider ${isCritical ? "text-red-600" : isUrgent ? "text-amber-600" : "text-blue-700"
                }`}
            >
              Decision Window
            </p>
            <p className="text-[11px] text-gray-500">
              72 hours to confirm satisfaction or raise dispute
            </p>
          </div>
        </div>

        <span
          className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold tracking-wide uppercase ${isCritical
            ? "bg-red-100 text-red-800 border border-red-200"
            : isUrgent
              ? "bg-amber-100 text-amber-800 border border-amber-200"
              : "bg-blue-100 text-blue-800 border border-blue-200"
            }`}
        >
          {isCritical ? "⚡ URGENT" : isUrgent ? "⚠️ HURRY" : "ℹ️ ACTIVE"}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-3 overflow-hidden rounded-full bg-gray-200/80 h-1.5">
        <div
          className={`h-full rounded-full transition-all duration-500 ${isCritical
            ? "bg-gradient-to-r from-red-500 to-rose-600"
            : isUrgent
              ? "bg-gradient-to-r from-amber-500 to-orange-500"
              : "bg-gradient-to-r from-blue-500 to-cyan-500"
            }`}
          style={{
            width: `${Math.max(0, Math.min(100, ((72 * 60 - ((hours || 0) * 60 + (minutes || 0))) / (72 * 60)) * 100))}%`,
          }}
        />
      </div>

      {/* Countdown Display */}
      <div className="grid grid-cols-3 gap-2.5 mb-3">
        {/* Hours */}
        <div className="bg-white rounded-xl p-2.5 text-center border border-gray-200/80 shadow-2xs">
          <div
            className={`text-xl sm:text-2xl font-black tabular-nums ${isCritical ? "text-red-600" : isUrgent ? "text-amber-600" : "text-blue-600"
              }`}
          >
            {String(hours || 0).padStart(2, "0")}
          </div>
          <p className="text-[10px] font-bold text-gray-400 mt-0.5 uppercase tracking-wider">
            Hours
          </p>
        </div>

        {/* Minutes */}
        <div className="bg-white rounded-xl p-2.5 text-center border border-gray-200/80 shadow-2xs">
          <div
            className={`text-xl sm:text-2xl font-black tabular-nums ${isCritical ? "text-red-600" : isUrgent ? "text-amber-600" : "text-blue-600"
              }`}
          >
            {String(minutes || 0).padStart(2, "0")}
          </div>
          <p className="text-[10px] font-bold text-gray-400 mt-0.5 uppercase tracking-wider">
            Minutes
          </p>
        </div>

        {/* Seconds */}
        <div className="bg-white rounded-xl p-2.5 text-center border border-gray-200/80 shadow-2xs">
          <div
            className={`text-xl sm:text-2xl font-black tabular-nums ${isCritical ? "text-red-600" : isUrgent ? "text-amber-600" : "text-blue-600"
              }`}
          >
            {String(seconds || 0).padStart(2, "0")}
          </div>
          <p className="text-[10px] font-bold text-gray-400 mt-0.5 uppercase tracking-wider">
            Seconds
          </p>
        </div>
      </div>

      {/* Notice Message */}
      <div className="rounded-lg p-2 bg-white/80 border border-gray-200/60 text-center">
        <p
          className={`text-xs font-medium ${isCritical ? "text-red-700" : isUrgent ? "text-amber-700" : "text-blue-800"
            }`}
        >
          {isCritical
            ? "🔴 Less than 1 hour left! Please confirm satisfaction or raise a dispute."
            : isUrgent
              ? "🟠 Less than 24 hours remaining. Please review your order."
              : "🔵 You have 72 hours from delivery to confirm satisfaction or raise a dispute."}
        </p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const SelectedQuotationView = ({
  quotation,
  refreshQuotations,
  refreshOrders,
  onApprovalSuccess,
}) => {
  const [updating, setUpdating] = useState(false);
  const { user } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [showBreakdownDetails, setShowBreakdownDetails] = useState(false);
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [activeBreakdownTab, setActiveBreakdownTab] = useState(
    quotation?.sampleDetails?.requested ? "sample" : "fullOrder",
  );
  const [lcFile, setLcFile] = useState(null);
  const [lcUploading, setLcUploading] = useState(false);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [lcDocument, setLcDocument] = useState(null);
  const [bankTransferFile, setBankTransferFile] = useState(null);
  const [bankTransferUploading, setBankTransferUploading] = useState(false);
  const [bankTransferDocument, setBankTransferDocument] = useState(null);
  const [loadingBankTransferStatus, setLoadingBankTransferStatus] = useState(false);
  const [loadingLCStatus, setLoadingLCStatus] = useState(false);
  const [fetchingBreakdown, setFetchingBreakdown] = useState(true);
  const [approvalAction, setApprovalAction] = useState(null); // "Approved" | "Rejected"
  const [buyerDecision, setBuyerDecision] = useState(null);
  const [fullOrderAction, setFullOrderAction] = useState(null);       // "SATISFIED" | "DISPUTE"
  const [fullOrderConfirmOpen, setFullOrderConfirmOpen] = useState(false);  // for Satisfied only
  const [returnDisputeModalOpen, setReturnDisputeModalOpen] = useState(false);
  const [decisionConfirmOpen, setDecisionConfirmOpen] = useState(false);
  const [approvalConfirmOpen, setApprovalConfirmOpen] = useState(false);
  const [selectedDispatchId, setSelectedDispatchId] = useState(null);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [mediaModalIndex, setMediaModalIndex] = useState(0);

  const [modalImages, setModalImages] = useState([]);
  const [modalVideos, setModalVideos] = useState([]);
  // Proforma Invoice Modal ─────────────────────────────────────
  const [proformaModalOpen, setProformaModalOpen] = useState(false);
  // ─────────────────────────────────────────────────────────────────────────
  const [showFullOrderDecisionModal, setShowFullOrderDecisionModal] = useState(false);
  const [fullOrderDecisionConfirmOpen, setFullOrderDecisionConfirmOpen] = useState(false);

  // bankTransferDocument state
  const [sampleBankTransferDocument, setSampleBankTransferDocument] = useState(null);
  const [loadingSampleBankTransferStatus, setLoadingSampleBankTransferStatus] = useState(false);

  const [declarationData, setDeclarationData] = useState(null);
  const [downloadingDeclaration, setDownloadingDeclaration] = useState(false);

  const [timeRemaining, setTimeRemaining] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false,
  });
  const [countdownDispatchId, setCountdownDispatchId] = useState(null);

  const lockScroll = mediaModalOpen || proformaModalOpen || approvalConfirmOpen || decisionConfirmOpen || fullOrderConfirmOpen || returnDisputeModalOpen || fullOrderDecisionConfirmOpen;
  useLockBodyScroll(lockScroll);

  const latestOFMImpression =
    quotation.ofm_Impressions && quotation.ofm_Impressions.length > 0
      ? [...quotation.ofm_Impressions].sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      )[0]
      : null;

  const getDispatchByType = (type) => {
    if (!quotation.dispatchDetails) return null;
    if (Array.isArray(quotation.dispatchDetails))
      return quotation.dispatchDetails.find((d) => d.type === type);
    return quotation.dispatchDetails[type?.toLowerCase()] || null;
  };

  const dispatchList = [];
  if (Array.isArray(quotation.dispatchDetails)) {
    quotation.dispatchDetails.forEach((d) => {
      if (["Dispatched", "Delivered"].includes(d.status)) dispatchList.push(d);
    });
  }

  // ── Media download helper (handles cross-origin Cloudinary URLs) ──────────
  const downloadMedia = async (url, idx) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const extension = blob.type.split("/")[1] || "jpg";
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `product-media-${idx + 1}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Download failed. Opening in new tab instead.");
      window.open(url, "_blank");
    }
  };

  const downloadAllMedia = async () => {
    const toastId = "dl-all";
    toast.loading(`Preparing zip for ${allMedia.length} files...`, { id: toastId });

    try {
      const zip = new JSZip();
      const folder = zip.folder("product-media");

      await Promise.all(
        allMedia.map(async (url, idx) => {
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            const extension = blob.type.split("/")[1]?.split("+")[0] || "jpg";
            const fileName = `product-media-${idx + 1}.${extension}`;
            folder.file(fileName, blob);
          } catch (err) {
            console.error(`Failed to fetch media ${idx + 1}:`, err);
          }
        })
      );

      toast.loading("Zipping files...", { id: toastId });
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipName = `product-media-${quotation.quotationUniqueNumber || quotation._id?.slice(-6) || "files"}.zip`;
      saveAs(zipBlob, zipName);

      toast.dismiss(toastId);
      toast.success(`Downloaded ${allMedia.length} files as zip ✅`);
    } catch (err) {
      toast.dismiss(toastId);
      console.error("Zip download failed:", err);
      toast.error("Zip creation failed. Try downloading individually.");
    }
  };

  const fetchPriceBreakdown = async () => {
    if (!quotation?._id) return;
    try {
      setFetchingBreakdown(true);
      const { data } = await axios.get(
        `/price-breakdown/quotation/${quotation._id}`,
      );
      if (data?.success) setPriceBreakdown(data.data);
    } catch (err) {
      console.error(
        "Breakdown fetch error:",
        err?.response?.data || err.message,
      );
    } finally {
      setFetchingBreakdown(false);
    }
  };

  const fetchLCStatus = async () => {
    try {
      if (!quotation?._id) return;

      setLoadingLCStatus(true);

      const { data } = await axios.post(
        "/lc/get-lc-by-quotation",
        {
          selectedQuotationId: quotation._id,
        }
      );

      if (data?.success && data?.data) {
        setLcDocument(data.data);
      } else {
        setLcDocument(null);
      }
    } catch (error) {
      console.error(
        "LC STATUS FETCH ERROR:",
        error?.response?.data || error.message
      );

      setLcDocument(null);
    } finally {
      setLoadingLCStatus(false);
    }
  };

  const fetchBankTransferStatus = async () => {
    try {
      if (!quotation?._id) return;

      setLoadingBankTransferStatus(true);

      const { data } = await axios.post(
        "/bank-transfer/get-by-quotation",
        {
          selectedQuotationId: quotation._id,
        }
      );

      if (data?.success && data?.data) {
        setBankTransferDocument(data.data);
      } else {
        setBankTransferDocument(null);
      }
    } catch (error) {
      console.error(
        "BANK TRANSFER STATUS FETCH ERROR:",
        error?.response?.data || error.message
      );
      setBankTransferDocument(null);
    } finally {
      setLoadingBankTransferStatus(false);
    }
  };

  // add new fetch function, right after fetchBankTransferStatus
  const fetchSampleBankTransferStatus = async () => {
    try {
      if (!quotation?._id) return;
      setLoadingSampleBankTransferStatus(true);
      const { data } = await axios.post("/bank-transfer/get-by-quotation", {
        selectedQuotationId: quotation._id,
        type: "Sample",
      });
      setSampleBankTransferDocument(data?.success && data?.data ? data.data : null);
    } catch {
      setSampleBankTransferDocument(null);
    } finally {
      setLoadingSampleBankTransferStatus(false);
    }
  };

  useEffect(() => {
    if (!quotation?._id) return;

    fetchPriceBreakdown();

    switch (quotation?.paymentDetails?.method) {
      case "Letter_Of_Credit":
        fetchLCStatus();
        setBankTransferDocument(null);
        break;

      case "Direct_Bank_Transfer":
        fetchBankTransferStatus();
        setLcDocument(null);
        break;

      default:
        setLcDocument(null);
        setBankTransferDocument(null);
        break;
    }
  }, [
    quotation?._id,
    quotation?.paymentDetails?.method,
  ]);

  // (does NOT replace the existing full-order one) — runs
  // independently of active tab so status is ready whenever Sample tab is opened
  useEffect(() => {
    if (!quotation?._id) return;
    if (quotation?.paymentDetails?.sample?.method === "Direct_Bank_Transfer") {
      fetchSampleBankTransferStatus();
    } else {
      setSampleBankTransferDocument(null);
    }
  }, [quotation?._id, quotation?.paymentDetails?.sample?.method]);

  useEffect(() => {
    // Find the first delivered full order dispatch
    const fullOrderDispatch = dispatchList.find(
      (d) => d.type === "Full_Order" && d.status === "Delivered"
    );

    if (!fullOrderDispatch?.deliveredAt) {
      setCountdownDispatchId(null);
      return;
    }

    setCountdownDispatchId(fullOrderDispatch._id);

    // Calculate remaining time from deliveredAt
    const calculateRemainingTime = () => {
      const deliveredTime = new Date(fullOrderDispatch.deliveredAt);
      const expiryTime = new Date(deliveredTime.getTime() + 72 * 60 * 60 * 1000); // 72 hours
      const now = new Date();

      const totalMilliseconds = expiryTime - now;

      if (totalMilliseconds <= 0) {
        setTimeRemaining({
          hours: 0,
          minutes: 0,
          seconds: 0,
          expired: true,
        });
        return;
      }

      const totalSeconds = Math.floor(totalMilliseconds / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      setTimeRemaining({
        hours,
        minutes,
        seconds,
        expired: false,
      });
    };

    // Calculate immediately
    calculateRemainingTime();

    // Then update every second
    const interval = setInterval(calculateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [dispatchList]);

  const hasSampleFlow = quotation?.sampleDetails?.requested === true || quotation?.invoice_details?.sample?.proforma_invoice;

  const breakdown = priceBreakdown?.[activeBreakdownTab]?.breakdown;

  const isSample = activeBreakdownTab === "sample";
  const transportInfo = quotation?.transportDetails?.find(
    (t) => t.charge_is_for === (isSample ? "Sample" : "Full_Order"),
  );
  const isTransportReady =
    transportInfo?.transportation_charges === "Available";
  const isOrderConfirmed = isSample
    ? !!quotation?.invoice_details?.sample?.proforma_invoice
    : !!quotation?.invoice_details?.full_order?.proforma_invoice;

  const lcStatus = lcDocument?.status || null;

  const isLCUploaded = !!lcDocument;

  const isLCVerified = lcStatus === "VERIFIED";
  const isLCRejected = lcStatus === "REJECTED";

  const bankTransferStatus = bankTransferDocument?.status || null;

  const isBankTransferUploaded = !!bankTransferDocument;

  const isBankTransferVerified = bankTransferStatus === "VERIFIED";
  const isBankTransferRejected = bankTransferStatus === "REJECTED";

  // Determine which payment method is selected
  const selectedPaymentMethod = quotation?.paymentDetails?.method;
  const isBankTransferOrder = selectedPaymentMethod === "Direct_Bank_Transfer";
  const isRazorpayOrder = selectedPaymentMethod === "Online";
  const isSampleBankTransferOrder =
    quotation?.paymentDetails?.sample?.method === "Direct_Bank_Transfer";


  // const currentTotal = breakdown?.totalAmount || 0;

  const packSize = breakdown?.packSizeWeight || 0;

  const totalWeight = breakdown?.totalWeightKg || 0;

  const formatPrice = (num) => {
    const n = Number(num || 0);
    if (isNaN(n)) return "0";
    return n % 1 !== 0
      ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : n.toLocaleString("en-IN");
  };

  const baseAmount = Number(breakdown?.baseAmount || 0);

  const transportationCharges = Number(
    breakdown?.transportationCharges || 0
  );

  const gstRate = Number(breakdown?.gstRate || 0);

  const gstAmount = Number(breakdown?.gstAmount || 0);

  const grandTotal = Number(breakdown?.totalAmount || 0);

  const commissionDetails = breakdown?.commissionDetails || {};

  const commissionType = commissionDetails?.commissionType || "-";

  const commissionValue = commissionDetails?.commission || 0;

  const commissionAmount =
    Number(commissionDetails?.commissionInAmount || 0);

  const paymentMethod =
    breakdown?.paymentDetails?.eligiblePaymentMethod || null;

  // ── LC status derived values ──────────────────────────────────────────────
  // Prefer the buyer's actual saved choice (set at confirmation time);
  // fall back to the admin/threshold default only if no choice has been recorded yet.
  const actualPaymentMode = quotation?.paymentDetails?.method;
  const isLCOrder = actualPaymentMode
    ? actualPaymentMode === "Letter_Of_Credit"
    : paymentMethod?.toLowerCase() === "manual";

  /* ── handlers (all original, untouched) ── */
  const handleConfirmDelivered = async () => {
    if (!selectedDispatchId) return;
    try {
      setUpdating(true);
      const { data } = await axios.patch(`/dispatch/${selectedDispatchId}`, {
        status: "Delivered",
      });
      if (data?.success) {
        toast.success("Marked as Delivered ✅");
        await refreshQuotations?.();
        await refreshOrders?.();
        setConfirmOpen(false);
        setSelectedDispatchId(null);
      } else {
        toast.error(data?.message || "Failed to update dispatch");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setUpdating(false);
    }
  };

  const handleSampleApproval = async (remark = "") => {
    if (!approvalAction) return;
    try {
      setUpdating(true);
      const { data } = await axios.patch(
        `/quotation/sample-approval/${quotation._id}`,
        { approval: approvalAction, remarks: remark },
      );

      let createdDeclaration = null;

      if (approvalAction === "Approved") {
        try {
          const { data: declarationRes } = await axios.post("/declaration", {
            reason: "Sample Approval Declaration",
            declaration_contents: [
              { content: "I confirm that I have physically verified the received sample." },
              { content: "I am satisfied with the product quality, packaging, specifications, and condition." },
              { content: "I understand that approving this sample will be treated as final acceptance for proceeding with the order." },
            ],
            e_sign: user?.e_signature,
            date: new Date(),
            orderId: quotation?.orderId?._id || quotation?.orderId,
            quotationId: quotation?._id,
          });

          if (declarationRes?.success && declarationRes?.data) {
            createdDeclaration = declarationRes.data; // needs _id/createdAt from backend
            setDeclarationData(createdDeclaration);
          }
        } catch (err) {
          toast.error("Declaration submission failed.");
          setUpdating(false);
          return;
        }
      }

      if (data?.success) {
        toast.success(`Sample ${approvalAction.toLowerCase()} successfully ✅`);
        await refreshQuotations?.();
        await refreshOrders?.();
        setApprovalConfirmOpen(false);
        setApprovalAction(null);
        setDeclarationChecked(false);
        if (approvalAction === "Approved") {
          onApprovalSuccess?.();

          // 👇 auto-download right after everything succeeds
          if (createdDeclaration) {
            try {
              await generateDeclarationPDF(createdDeclaration, user);
            } catch (pdfErr) {
              console.error("Auto PDF download failed:", pdfErr);
              toast.error("Declaration created, but PDF download failed. You can retry below.");
            }
          }
        }
      } else {
        toast.error(data?.message || "Failed to update approval");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setUpdating(false);
    }
  };

  const handleBuyerDecision = async () => {
    if (!buyerDecision) return;
    try {
      setUpdating(true);
      let data;

      if (buyerDecision === "Proceed_Full_Order") {
        // ✅ FIXED: Always send isSampleRequested = false for full order
        // This handles BOTH:
        // 1. Direct full order (no sample)
        // 2. Post-sample full order (after sample approval)
        const response = await axios.post(
          `/transport-charge/${quotation._id}/express-interest`,
          {
            charge_is_for: "Full_Order",
            isSampleRequested: false,  // ← ALWAYS false for full order
          }
        );

        data = response.data;

      } else if (buyerDecision === "Close") {
        // Handle close decision
        const response = await axios.patch(
          `/quotation-v2/save-buyer-decision/${quotation._id}`,
          { decision: "Rejected" }
        );
        data = response.data;
      } else {
        const response = await axios.patch(
          `/quotation-v2/save-buyer-decision/${quotation._id}`,
          { decision: buyerDecision }
        );
        data = response.data;
      }

      if (data?.success) {
        if (buyerDecision === "Close") {
          toast.success("Order closed successfully ❌");
        } else if (buyerDecision === "Proceed_Full_Order") {
          toast.success("Proceeding to full order 🚀");
        }

        await refreshQuotations?.();
        await refreshOrders?.();
        setDecisionConfirmOpen(false);
        setBuyerDecision(null);
      } else {
        toast.error(data?.message || "Failed to save decision");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setUpdating(false);
    }
  };

  const handleFullOrderAction = async () => {
    // Satisfied path only — DISPUTE now goes through handleRaiseReturnDispute
    if (!fullOrderAction || fullOrderAction !== "SATISFIED") return;
    try {
      setUpdating(true);
      const { data } = await axios.patch(
        `/quotation-v2/mark-satisfied-full-order/${quotation._id}`
      );
      if (data?.success) {
        toast.success("Marked as satisfied ✅");
        await refreshQuotations?.();
        await refreshOrders?.();
        setFullOrderConfirmOpen(false);
        setFullOrderAction(null);
      } else {
        toast.error(data?.message || "Action failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setUpdating(false);
    }
  };

  const handleRaiseReturnDispute = async ({
    returnType,
    reason,
    packets,
    partialReturnPackets,
  }) => {
    try {
      setUpdating(true);
      const effectivePackets =
        partialReturnPackets !== undefined && partialReturnPackets !== null
          ? partialReturnPackets
          : packets;
      const payload = {
        orderId: quotation.orderId?._id || quotation.orderId,
        quotationId: quotation._id,
        returnType,
        reason,
        ...(returnType === "Partial" && effectivePackets
          ? {
              partialReturnPackets: Number(effectivePackets),
              packets: Number(effectivePackets),
            }
          : {}),
      };
      const { data } = await axios.post("/return/create", payload);
      if (data?.success) {
        toast.success("Return dispute raised successfully ⚠️");
        await refreshQuotations?.();
        await refreshOrders?.();
        setReturnDisputeModalOpen(false);
      } else {
        toast.error(data?.message || "Failed to raise dispute");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setUpdating(false);
    }
  };

  /* ── derived ── */
  const allMedia = [
    ...(quotation.media?.images || []),
    ...(quotation.media?.videos || []),
  ];
  const isVideoUrl = (url) =>
    url?.includes(".mp4") || url?.includes(".webm") || url?.includes("video");
  const sampleApprovalColor =
    quotation.sampleDetails?.approval === "Approved"
      ? "green"
      : quotation.sampleDetails?.approval === "Rejected"
        ? "red"
        : "yellow";

  /* ═════════════════════════════════════════════════════════
     RENDER
  ═════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-4 pb-6">
      {/* ── ① HERO BANNER ─────────────────────────────────── */}
      <div className="relative rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-400" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%)",
            backgroundSize: "12px 12px",
          }}
        />
        <div className="relative z-10 px-4 sm:px-6 py-5 sm:py-6">
          {/* top row */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border border-white/30">
                <FiCheck className="text-white text-xl" strokeWidth={3} />
              </div>
              <div>
                <p className="text-[10px] font-black text-white/60 uppercase tracking-[0.15em]">
                  Selected Quotation
                </p>
                <h2 className="text-lg sm:text-xl font-extrabold text-white leading-tight">
                  {quotation.quotationUniqueNumber ||
                    `#${quotation._id?.slice(-6)}`}
                </h2>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-[11px] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                {isSample ? "Sample Flow" : "Full Order"}
              </span>
              {quotation.batch_Lot_Number && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/80 text-[11px] font-semibold">
                  <HiOutlineHashtag size={11} />
                  {quotation.batch_Lot_Number}
                </span>
              )}
            </div>
          </div>

          {/* price + qty strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
            <div className="col-span-2 sm:col-span-1 bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
              <p className="text-[9px] font-black text-white/50 uppercase tracking-widest mb-0.5">
                Unit Price
              </p>
              <p className="text-2xl sm:text-3xl font-extrabold text-white leading-none">
                {quotation.currency} {quotation.pricing?.commissionAdjustedAmount?.toFixed(2) || 0}
              </p>
              <p className="text-[11px] text-white/60 mt-0.5">
                per {quotation.standardizedUnit}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/15">
              <p className="text-[9px] font-black text-white/50 uppercase tracking-widest mb-0.5">
                Quantity
              </p>
              <p className="text-xl font-extrabold text-white leading-none">
                {quotation.quantity?.volume || 0}
                <span className="text-sm font-semibold text-white/60 ml-1">
                  {quotation.standardizedUnit}
                </span>
              </p>
              <p className="text-[11px] text-white/50 mt-0.5">
                {quotation.quantity?.isAvailableFull
                  ? "Full qty available"
                  : "Partial"}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/15">
              <p className="text-[9px] font-black text-white/50 uppercase tracking-widest mb-0.5">
                Sample
              </p>
              <p className="text-sm font-extrabold text-white leading-none mt-1">
                {quotation.sampleDetails?.requested
                  ? "Requested"
                  : "Not Requested"}
              </p>
              {quotation.sampleDetails?.requested && (
                <p
                  className={`text-[11px] mt-0.5 font-semibold ${quotation.sampleDetails?.approval === "Approved"
                    ? "text-emerald-200"
                    : quotation.sampleDetails?.approval === "Rejected"
                      ? "text-red-200"
                      : "text-amber-200"
                    }`}
                >
                  {quotation.sampleDetails?.approval || "Pending"}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── DISPUTE ALERT (shown only if this quotation is in a dispute stage) ── */}
      {isDisputeOrder(quotation.quotationStage) && (
        <DisputeAlertBanner currentStage={quotation.quotationStage} />
      )}

      {/* ── CLIENT VISIT PASS FOR BUYER VERIFICATION (Dispute Stage Only) ── */}
      {(() => {
        const pass =
          (quotation.clientVisitPass?.passType === "Dispute" ? quotation.clientVisitPass : null) ||
          quotation.returnDispute?.clientVisitPassId ||
          (quotation.clientVisitPassId?.passType === "Dispute" ? quotation.clientVisitPassId : null) ||
          (quotation.orderId?.clientVisitPassId?.passType === "Dispute" ? quotation.orderId?.clientVisitPassId : null);
        return (pass && pass.passType === "Dispute") ? <ClientVisitPassCard pass={pass} /> : null;
      })()}

      {/* ── DISPUTE RESOLUTION TIMELINE ── */}
      {isDisputeOrder(quotation.quotationStage) && (
        <Block label="Dispute Resolution Timeline" icon={MdOutlineVerified}>
          <DisputeStageTimeline currentStage={quotation.quotationStage} />
          <div className="mt-5 flex justify-center">
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-amber-50 text-amber-600 border border-amber-200">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
              Current Status:{" "}
              {DISPUTE_STAGES.find((s) => s.key === quotation.quotationStage)?.label ||
                quotation.quotationStage?.replace(/_/g, " ")}
            </span>
          </div>
        </Block>
      )}

      {/* ── RETURN DISPATCH CARD (Partial or Full Return) ── */}
      {(quotation.quotationStage === "Partial_Order_Returned" ||
        quotation.quotationStage === "Full_Order_Returned" ||
        quotation.orderId?.stages === "Partial_Order_Returned" ||
        quotation.orderId?.stages === "Full_Order_Returned") && (
        <ReturnDispatchCard
          orderId={quotation.orderId?._id || quotation.orderId}
          orderNumber={quotation.orderId?.orderNumber}
          quotationNumber={quotation.quotationUniqueNumber}
          stage={quotation.quotationStage || quotation.orderId?.stages}
          onDispatchSuccess={() => {
            refreshQuotations?.();
            refreshOrders?.();
          }}
        />
      )}

      {/* ── ② PACK SIZE + LOGISTICS (side by side on desktop) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pack Size */}
        <Block label="Pack Size & Dimensions" icon={TbRuler}>
          <div className="grid grid-cols-2 gap-2.5 mb-3">
            <MetricTile
              label="Weight"
              value={quotation.packSize?.weight ?? "—"}
              unit={quotation.packSize?.unit || quotation.standardizedUnit || "Kg"}
              color="indigo"
            // icon={TbWeight}
            />
            <MetricTile
              label="Length"
              value={quotation.packSize?.dimensions?.length ?? "—"}
              unit={quotation.packSize?.dimensions?.measured || "cm"}
              color="gray"
            />
            <MetricTile
              label="Breadth"
              value={quotation.packSize?.dimensions?.breadth ?? "—"}
              unit={quotation.packSize?.dimensions?.measured || "cm"}
              color="gray"
            />
            <MetricTile
              label="Height"
              value={quotation.packSize?.dimensions?.height ?? "—"}
              unit={quotation.packSize?.dimensions?.measured || "cm"}
              color="gray"
            />
          </div>
          <div className="flex items-center justify-center gap-1.5 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100 text-xs">
            <span className="text-gray-400">L</span>
            <span className="font-bold text-gray-700">
              {quotation.packSize?.dimensions?.length ?? "—"}
            </span>
            <span className="text-gray-300">×</span>
            <span className="text-gray-400">B</span>
            <span className="font-bold text-gray-700">
              {quotation.packSize?.dimensions?.breadth ?? "—"}
            </span>
            <span className="text-gray-300">×</span>
            <span className="text-gray-400">H</span>
            <span className="font-bold text-gray-700">
              {quotation.packSize?.dimensions?.height ?? "—"}
            </span>
            <span className="text-gray-400 ml-0.5">
              {quotation.packSize?.dimensions?.measured || "cm"}
            </span>
            <span className="mx-1.5 text-gray-200">·</span>
            <span className="font-bold text-indigo-700">
              {quotation.packSize?.weight ?? "—"} {quotation.packSize?.unit || quotation.standardizedUnit || "Kg"}
            </span>
          </div>
        </Block>

        {/* Logistics */}
        <Block label="Logistics" icon={MdOutlineLocalShipping}>
          <div className="flex items-start gap-3 p-3.5 rounded-xl bg-orange-50 border border-orange-100 mb-3">
            <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FiCalendar className="text-orange-600" size={14} />
            </div>
            <div>
              <p className="text-[10px] font-black text-orange-400 uppercase tracking-widest">
                Holding Period
              </p>
              <p className="text-sm font-bold text-orange-800 mt-0.5 leading-snug">
                {quotation.holdingPeriod?.from
                  ? new Date(quotation.holdingPeriod.from).toLocaleDateString(
                    "en-GB",
                    { day: "numeric", month: "short", year: "numeric" },
                  )
                  : "N/A"}
                <span className="text-orange-400 mx-1.5 font-normal">→</span>
                {quotation.holdingPeriod?.to
                  ? new Date(quotation.holdingPeriod.to).toLocaleDateString(
                    "en-GB",
                    { day: "numeric", month: "short", year: "numeric" },
                  )
                  : "N/A"}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3.5 rounded-xl bg-purple-50 border border-purple-100">
            <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <RiMapPin2Line className="text-purple-600" size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-0.5">
                Pickup Location
              </p>
              <p className="text-sm font-bold text-purple-900 leading-snug">
                {quotation.pickUpAddress?.addressLine1 || "N/A"}
              </p>
              {quotation.pickUpAddress?.addressLine2 && (
                <p className="text-xs text-purple-700 mt-0.5">
                  {quotation.pickUpAddress.addressLine2}
                </p>
              )}
              <p className="text-xs text-purple-400 mt-0.5">
                {[
                  quotation.pickUpAddress?.city,
                  quotation.pickUpAddress?.state,
                  quotation.pickUpAddress?.country,
                  quotation.pickUpAddress?.pincode,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </div>
          </div>
        </Block>
      </div>

      {/* ── ③ ORDER STATUS ────────────────────────────────── */}
      <Block label="Order Status" icon={MdOutlineVerified}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex flex-col gap-1.5 p-3.5 rounded-xl bg-gray-50 border border-gray-100">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Batch / Lot
            </p>
            <p className="text-sm font-bold text-gray-800 font-mono">
              {quotation.batch_Lot_Number || "—"}
            </p>
          </div>
          <div className="flex flex-col gap-1.5 p-3.5 rounded-xl bg-gray-50 border border-gray-100">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Sample Requested
            </p>
            <Pill
              label={quotation.sampleDetails?.requested ? "Yes" : "No"}
              color={quotation.sampleDetails?.requested ? "green" : "gray"}
            />
          </div>
          {quotation.sampleDetails?.requested && (
            <div className="flex flex-col gap-1.5 p-3.5 rounded-xl bg-gray-50 border border-gray-100">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Sample Approval
              </p>
              <Pill
                label={quotation.sampleDetails?.approval || "Pending"}
                color={sampleApprovalColor}
              />
            </div>
          )}
        </div>
      </Block>

      {/* ── ④ PRICING SUMMARY ─────────────────────────────── */}
      <Block label="Pricing Summary" icon={IoDocumentTextOutline} accent>
        {fetchingBreakdown ? (
          <div className="max-h-[260px] overflow-hidden flex items-center justify-center">
            <AppLoader message="Loading pricing summary..." />
          </div>
        ) : !breakdown ? (
          <div className="text-center py-10 flex flex-col items-center gap-2">
            <span className="text-3xl opacity-30">📊</span>
            <p className="text-sm text-gray-400">
              Pricing breakdown not available yet
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">

            {/* ── TAB SWITCHER — TOP ───────────────────────────── */}
            {hasSampleFlow && (
              <div className="bg-gray-200 p-1 rounded-2xl flex items-center mb-4">
                <button
                  onClick={() => setActiveBreakdownTab("sample")}
                  className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all duration-200 ${activeBreakdownTab === "sample"
                    ? "bg-white text-primaryColor shadow-sm"
                    : "text-gray-500"
                    }`}
                >
                  Sample
                </button>
                <button
                  onClick={() => setActiveBreakdownTab("fullOrder")}
                  className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all duration-200 ${activeBreakdownTab === "fullOrder"
                    ? "bg-white text-primaryColor shadow-sm"
                    : "text-gray-500"
                    }`}
                >
                  Full Order
                </button>
              </div>
            )}

            {/* Base Price */}
            <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
              <div className="flex items-center gap-2.5">
                <span className="text-gray-400"><FiPackage size={14} /></span>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {isSample ? "Sample Price" : "Base Price"}
                  </p>
                  <p className="text-xs text-gray-400">
                    {`${breakdown?.quantity || 0} unit × ₹${Number(breakdown?.pricePerKg || 0).toLocaleString()}`}
                  </p>
                </div>
              </div>
              <p className="text-sm font-bold text-gray-900 flex-shrink-0">
                ₹{formatPrice(breakdown?.baseAmount || 0)}
              </p>
            </div>

            {/* GST */}
            <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
              <div className="flex items-center gap-2.5">
                <span className="text-gray-400">
                  <IoDocumentTextOutline size={14} />
                </span>

                <div>
                  <p className="text-sm font-semibold text-gray-800">GST</p>
                  <p className="text-xs text-gray-400">
                    {gstRate}% applied
                  </p>
                </div>
              </div>

              <p className="text-sm font-bold text-gray-900 flex-shrink-0">
                ₹{formatPrice(gstAmount)}
              </p>
            </div>

            {/* Commission */}
            {/* <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
              <div className="flex items-center gap-2.5">
                <span className="text-gray-400">
                  <FiDollarSign size={14} />
                </span>

                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Platform Commission
                  </p>

                  <p className="text-xs text-gray-400">
                    {commissionType === "Percentage"
                      ? `${commissionValue}% applied`
                      : `Fixed Commission`}
                  </p>
                </div>
              </div>

              <p className="text-sm font-bold text-gray-900 flex-shrink-0">
                ₹{formatPrice(commissionAmount)}
              </p>
            </div> */}

            {/* Transport */}
            <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
              <div className="flex items-center gap-2.5">
                <FiTruck className="text-primaryColor flex-shrink-0" size={14} />
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {isSample ? "Courier Charge" : "Freight & Forwarding"}
                  </p>
                  <p className="text-xs text-gray-400">
                    {isTransportReady ? "Admin verified charges" : "Waiting for admin update"}
                  </p>
                </div>
              </div>
              {isTransportReady ? (
                <p className="text-sm font-bold text-gray-900 flex-shrink-0">
                  ₹{formatPrice(breakdown?.transportationCharges || 0)}
                </p>
              ) : (
                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg flex-shrink-0">
                  NOT AVAILABLE
                </span>
              )}
            </div>

            {/* Grand Total */}
            <div className="relative rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-primaryColor to-primaryColor/80" />
              <div className="relative z-10 flex items-center justify-between gap-3 px-5 py-4">
                <div>
                  <p className="text-xs text-white/70 font-semibold">Estimated Total</p>
                  <p className="text-[11px] text-white/40 mt-0.5">Including other charges </p>
                </div>
                <p className="text-2xl sm:text-3xl font-extrabold text-white">
                  ₹{formatPrice(grandTotal)}
                </p>
              </div>
            </div>

            {/* ── SAMPLE TAB: Proceed button — modal handles Razorpay, LC, and Bank Transfer ── */}
            {activeBreakdownTab === "sample" && (
              <div className="mt-3">
                {isOrderConfirmed && isLCOrder ? (
                  /* ── Sample was LC order: show LC upload section ── */
                  <LCUploadSection
                    quotation={quotation}
                    lcDocument={lcDocument}
                    lcFile={lcFile}
                    setLcFile={setLcFile}
                    lcUploading={lcUploading}
                    setLcUploading={setLcUploading}
                    lcStatus={lcStatus}
                    isLCVerified={isLCVerified}
                    isLCUploaded={isLCUploaded}
                    isLCRejected={isLCRejected}
                    refreshQuotations={refreshQuotations}
                  />
                ) : isOrderConfirmed && isSampleBankTransferOrder ? (
                  /* ── Sample was Bank Transfer order: STATUS ONLY.
                       Actual upload happens once in bulk from the sample
                       quotations list view, not per-quotation here. ── */
                  <SampleBankTransferStatusCard
                    bankTransferDocument={sampleBankTransferDocument}
                  />
                ) : (
                  <div
                    className={`w-full rounded-2xl py-4 px-5 font-bold text-sm flex items-center justify-center gap-2 ${isOrderConfirmed
                      ? "bg-green-600 text-white cursor-default"
                      : !isTransportReady
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed" /* payment action hidden */
                      }`}
                  >
                    {isOrderConfirmed ? (
                      <><FiCheck />Sample Order Confirmed</>
                    ) : !isTransportReady ? (
                      <><FiTruck />Dear Buyer, Kindly wait for the QQT Admin to provide transport charges for your order. Once it is available, you can proceed for payment.</>
                    ) : <><FiTruck />Dear buyer, QQT admin has updated the transport charges. Now you can proceed with the order.</>}
                  </div>
                )}
              </div>
            )}

            {/* ── FULL ORDER TAB: Express Interest / LC / Razorpay ── */}
            {activeBreakdownTab === "fullOrder" && (
              <div className="mt-3 space-y-3">
                {/* Only show full order actions after sample is approved and buyer chose to proceed */}
                {quotation?.sampleDetails?.requested &&
                  quotation?.sampleDetails?.approval !== "Approved" ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                    <p className="text-xs font-semibold text-amber-700">
                      ⏳ Full order becomes available after sample is approved
                    </p>
                  </div>
                ) : quotation?.sampleDetails?.requested &&
                  quotation?.sampleDetails?.approval === "Approved" &&
                  quotation?.buyerDecision !== "Proceed_Full_Order" ? (
                  /* ── Proceed Full Order + Close buttons (POST-SAMPLE FLOW) ── */
                  <div className="space-y-2">
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-3">
                      <p className="text-xs text-green-700 font-semibold">
                        ✅ Sample approved! Ready to proceed with full order.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      {/* <button
                        disabled={updating}
                        onClick={() => {
                          setBuyerDecision("Close");
                          setDecisionConfirmOpen(true);
                        }}
                        className="flex-1 py-3.5 border-2 border-gray-200 text-gray-600 rounded-2xl font-semibold text-sm hover:bg-gray-50 transition"
                      >
                        ✕ Close
                      </button> */}
                      <button
                        disabled={updating}
                        onClick={() => {
                          setBuyerDecision("Proceed_Full_Order");
                          setDecisionConfirmOpen(true);
                        }}
                        className="flex-[2] flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-primaryColor to-[#c44a0b] text-white rounded-2xl font-bold text-sm hover:opacity-90 transition shadow-lg shadow-primaryColor/30"
                      >
                        {updating ? (
                          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing...</>
                        ) : (
                          <>🚀 Proceed to Full Order</>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  // ── AFTER PROCEEDING: Show waiting for transport or payment options ──
                  quotation?.buyerDecision === "Proceed_Full_Order" ? (
                    <div className="space-y-3">
                      {!isTransportReady ? (
                        /* Waiting for Transport */
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <FiTruck className="text-amber-700" size={20} />
                            <p className="text-sm font-semibold text-amber-700">
                              ⏳ Dear Buyer, Kindly wait for the QQT Admin to provide transport charges for your order. Once it is available, you can proceed for payment.
                            </p>
                          </div>
                          <p className="text-xs text-amber-600">
                            Admin is calculating shipping charges. Please wait...
                          </p>
                        </div>
                      ) : isOrderConfirmed && isLCOrder ? (
                        /* ── LC confirmed: show upload section ── */
                        <LCUploadSection
                          quotation={quotation}
                          lcDocument={lcDocument}
                          lcFile={lcFile}
                          setLcFile={setLcFile}
                          lcUploading={lcUploading}
                          setLcUploading={setLcUploading}
                          lcStatus={lcStatus}
                          isLCVerified={isLCVerified}
                          isLCUploaded={isLCUploaded}
                          isLCRejected={isLCRejected}
                          refreshQuotations={refreshQuotations}
                        />
                      ) : isOrderConfirmed && isBankTransferOrder ? (
                        /* ── Bank Transfer confirmed: show upload section ── */
                        <BankTransferUploadSection
                          quotation={quotation}
                          bankTransferDocument={bankTransferDocument}
                          bankTransferFile={bankTransferFile}
                          setBankTransferFile={setBankTransferFile}
                          bankTransferUploading={bankTransferUploading}
                          setBankTransferUploading={setBankTransferUploading}
                          bankTransferStatus={bankTransferStatus}
                          isBankTransferVerified={isBankTransferVerified}
                          isBankTransferUploaded={isBankTransferUploaded}
                          isBankTransferRejected={isBankTransferRejected}
                          refreshQuotations={refreshQuotations}
                          grandTotal={grandTotal}
                        />
                      ) : (
                        <button
                          disabled={!isTransportReady || isOrderConfirmed || processingOrder}
                          onClick={() => setProformaModalOpen(true)}
                          className={`w-full rounded-2xl py-4 px-5 font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${isOrderConfirmed
                            ? "bg-green-600 text-white cursor-default"
                            : !isTransportReady
                              ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                              : "bg-primaryColor hover:opacity-90 text-white"
                            }`}
                        >
                          {processingOrder ? (
                            <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing...</>
                          ) : isOrderConfirmed ? (
                            <><FiCheck />Order Confirmed</>
                          ) : !isTransportReady ? (
                            <><FiTruck />Dear Buyer, Kindly wait for the QQT Admin to provide transport charges for your order. Once it is available, you can proceed for payment.</>
                          ) : (
                            <>
                              <FiArrowRight />
                              {isLCOrder ? "Dear buyer, QQT admin has updated the transport charges. Now you can proceed with the LC" : "Dear buyer, QQT admin has updated the transport charges. Now you can proceed with the payment"}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  ) : (
                    // ── No sample flow: Show payment options directly after expressing interest ──
                    <div className="space-y-3">
                      {!isTransportReady ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <FiTruck className="text-amber-700" size={20} />
                            <p className="text-sm font-semibold text-amber-700">
                              ⏳ Dear Buyer, Kindly wait for the QQT Admin to provide transport charges for your order. Once it is available, you can proceed for payment.
                            </p>
                          </div>
                          <p className="text-xs text-amber-600">
                            Admin is calculating shipping charges. Please wait...
                          </p>
                        </div>
                      ) : isOrderConfirmed && isLCOrder ? (
                        /* ── LC confirmed for direct full order: show upload section ── */
                        <LCUploadSection
                          quotation={quotation}
                          lcFile={lcFile}
                          setLcFile={setLcFile}
                          lcUploading={lcUploading}
                          setLcUploading={setLcUploading}
                          lcStatus={lcStatus}
                          isLCVerified={isLCVerified}
                          isLCUploaded={isLCUploaded}
                          isLCRejected={isLCRejected}
                          refreshQuotations={refreshQuotations}
                        />
                      ) : (
                        /* ── Modal handles Razorpay vs LC internally ── */
                        <button
                          disabled={!isTransportReady || isOrderConfirmed || processingOrder}
                          onClick={() => setProformaModalOpen(true)}
                          className={`w-full rounded-2xl py-4 px-5 font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${isOrderConfirmed
                            ? "bg-green-600 text-white cursor-default"
                            : !isTransportReady
                              ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                              : "bg-primaryColor hover:opacity-90 text-white"
                            }`}
                        >
                          {processingOrder ? (
                            <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing...</>
                          ) : isOrderConfirmed ? (
                            <><FiCheck />Order Confirmed</>
                          ) : !isTransportReady ? (
                            <><FiTruck />Dear Buyer, Kindly wait for the QQT Admin to provide transport charges for your order. Once it is available, you can proceed for payment.</>
                          ) : (
                            <>
                              <FiArrowRight />
                              {isLCOrder ? "Dear buyer, QQT admin has updated the transport charges. Now you can proceed with the LC" : "Dear buyer, QQT admin has updated the transport charges. Now you can proceed with the payment"}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )
                )}
              </div>
            )}

            {/* VIEW BREAKDOWN TOGGLE */}
            <button
              onClick={() => setShowBreakdownDetails(!showBreakdownDetails)}
              className="w-full mt-3 border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-all duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TbFileFilled className="text-primaryColor" />
                  <span className="text-sm font-semibold text-gray-700">
                    {showBreakdownDetails ? "Hide Detailed Breakdown" : "View Detailed Breakdown"}
                  </span>
                </div>
                <span className="text-primaryColor text-lg font-bold">
                  {showBreakdownDetails ? "−" : "+"}
                </span>
              </div>
            </button>

            {/* EXPANDABLE BREAKDOWN — tab switcher REMOVED from here (moved to top) */}
            <div className={`overflow-hidden transition-all duration-300 ${showBreakdownDetails ? "max-h-[2000px] opacity-100 mt-4" : "max-h-0 opacity-0"}`}>
              <div className="border border-gray-100 rounded-3xl bg-gray-50 p-3 md:p-5">

                {/* VOLUME & PACKAGING */}
                <div className="bg-white rounded-2xl border border-gray-100 p-3 md:p-5 mb-5">
                  <h3 className="text-base font-bold text-gray-900 mb-5">Volume & Packaging</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Total Volume</p>
                      <p className="text-sm font-bold text-gray-900 mt-2">{breakdown?.totalWeightKg || 0} {quotation.standardizedUnit || "Kg"}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Pack Size</p>
                      <p className="text-sm font-bold text-gray-900 mt-2">{breakdown?.packSizeWeight || 0} {quotation.packSize?.unit || quotation.standardizedUnit || "Kg"}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Total Units</p>
                      <p className="text-sm font-bold text-gray-900 mt-2">{breakdown?.quantity || 0}</p>
                    </div>
                  </div>
                  <div className="border-t border-dashed border-gray-200 my-5" />
                  <div className="rounded-2xl bg-primaryColor/5 border border-primaryColor/10 p-4">
                    <p className="text-xs text-gray-600 leading-6">
                      Packaging is selected as{" "}
                      <span className="font-semibold text-gray-900">{breakdown?.packSizeWeight || 0} {quotation.packSize?.unit || quotation.standardizedUnit || "Kg"}</span>{" "}
                      per unit. Total{" "}
                      <span className="font-semibold text-gray-900">{breakdown?.quantity || 0}</span>{" "}
                      units make up{" "}
                      <span className="font-semibold text-gray-900">{breakdown?.totalWeightKg || 0} {quotation.standardizedUnit || "Kg"}</span>.
                    </p>
                  </div>
                </div>

                {/* PAYMENT SUMMARY */}
                <div className="bg-white rounded-2xl border border-gray-100 p-3 md:p-5">
                  <h3 className="text-base font-bold text-gray-900 mb-5">Payment Summary</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{isSample ? "Sample Base Price" : "Order Base Price"}</p>
                        <p className="text-xs text-gray-500 mt-1">{breakdown?.totalWeightKg || 0} Kg × ₹{formatPrice(breakdown?.pricePerKg || 0)}/Kg</p>
                      </div>
                      <p className="text-sm font-bold text-gray-900">₹{formatPrice(breakdown?.baseAmount || 0)}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">GST</p>
                        <p className="text-xs text-gray-500 mt-1">{gstRate}% applied</p>
                      </div>

                      <p className="text-sm font-bold text-gray-900">
                        ₹{formatPrice(gstAmount)}
                      </p>
                    </div>
                    {/* <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          Platform Commission
                        </p>

                        <p className="text-xs text-gray-500 mt-1">
                          {commissionType === "Percentage"
                            ? `${commissionValue}% applied`
                            : "Fixed Commission"}
                        </p>
                      </div>

                      <p className="text-sm font-bold text-gray-900">
                        ₹{formatPrice(commissionAmount)}
                      </p>
                    </div> */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">Logistics & Transport</p>
                        <p className="text-xs text-gray-500 mt-1">Admin Verified Charges</p>
                      </div>
                      {isTransportReady ? (
                        <p className="text-sm font-bold text-gray-900">₹{formatPrice(breakdown?.transportationCharges || 0)}</p>
                      ) : (
                        <div className="px-3 py-1 rounded-lg bg-yellow-50 border border-yellow-200">
                          <span className="text-[10px] font-bold text-yellow-700">PENDING</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        Payment Method
                      </p>

                      <p className="text-xs text-gray-500 mt-1">
                        Backend configured payment flow
                      </p>
                    </div>

                    <div
                      className={`px-3 py-1 rounded-lg text-xs font-bold ${paymentMethod === "razorpay"
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-blue-50 text-blue-700 border border-blue-200"
                        }`}
                    >
                      {paymentMethod === "razorpay"
                        ? "RAZORPAY"
                        : "MANUAL / LC"}
                    </div>
                  </div>
                  <div className="border-t border-dashed border-gray-200 my-5" />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-extrabold text-gray-900">Grand Total</p>
                      <p className="text-xs text-gray-500 mt-1">Incl. all taxes & logistics</p>
                    </div>
                    <p className="text-2xl font-extrabold text-primaryColor">₹{formatPrice(grandTotal)}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
      </Block>

      {/* ── ⑤ INVOICES ────────────────────────────────────── */}
      <Block label="Invoices" icon={TbFileInvoice}>
        {!quotation.invoice_details?.sample?.proforma_invoice &&
          !quotation.invoice_details?.full_order?.proforma_invoice ? (
          <p className="text-xs text-gray-400 text-center py-5">
            No invoices available yet
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* SAMPLE INVOICE */}
            {quotation.invoice_details?.sample?.proforma_invoice && (
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100">
                  <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest">
                    Sample Invoice
                  </p>
                </div>
                <div className="px-4 py-1 divide-y divide-gray-50">
                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primaryColor" />
                      <span className="text-xs font-semibold text-gray-600">
                        Proforma
                      </span>
                    </div>
                    <button
                      onClick={() =>
                        window.open(
                          quotation.invoice_details.sample.proforma_invoice,
                          "_blank",
                        )
                      }
                      className="text-xs font-bold text-primaryColor hover:underline"
                    >
                      View →
                    </button>
                  </div>
                  {/* Original (only if exists) */}
                  {quotation.invoice_details?.sample?.original_invoice && (
                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span className="text-xs font-semibold text-gray-600">
                          Original
                        </span>
                      </div>
                      <button
                        onClick={() =>
                          window.open(
                            quotation.invoice_details.sample.original_invoice,
                            "_blank",
                          )
                        }
                        className="text-xs font-bold text-emerald-600 hover:underline"
                      >
                        View →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* FULL ORDER INVOICE */}
            {quotation.invoice_details?.full_order?.proforma_invoice && (
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-4 py-2.5 bg-emerald-50 border-b border-emerald-100">
                  <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
                    Full Order Invoice
                  </p>
                </div>
                <div className="px-4 py-1 divide-y divide-gray-50">
                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primaryColor" />
                      <span className="text-xs font-semibold text-gray-600">
                        Proforma
                      </span>
                    </div>
                    <button
                      onClick={() =>
                        window.open(
                          quotation.invoice_details.full_order.proforma_invoice,
                          "_blank",
                        )
                      }
                      className="text-xs font-bold text-primaryColor hover:underline"
                    >
                      View →
                    </button>
                  </div>
                  {/* Original */}
                  {quotation.invoice_details?.full_order?.original_invoice && (
                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span className="text-xs font-semibold text-gray-600">
                          Original
                        </span>
                      </div>
                      <button
                        onClick={() =>
                          window.open(
                            quotation.invoice_details.full_order
                              .original_invoice,
                            "_blank",
                          )
                        }
                        className="text-xs font-bold text-emerald-600 hover:underline"
                      >
                        View →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Block>

      {/* ── ⑥ PRODUCT MEDIA ───────────────────────────────── */}
      {allMedia.length > 0 && (
        <Block label="Product Media" icon={MdOutlinePhotoLibrary}>
          <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-5 gap-2.5">
            {allMedia.map((url, idx) => {
              const isVideo = isVideoUrl(url);
              return (
                <div key={idx} className="relative group aspect-square">
                  {/* ── View button (original behaviour) ── */}
                  <button
                    onClick={() => {
                      setModalImages(quotation.media?.images || []);
                      setModalVideos(quotation.media?.videos || []);
                      setMediaModalIndex(idx);
                      setMediaModalOpen(true);
                    }}
                    className="w-full h-full rounded-xl overflow-hidden border-2 border-transparent group-hover:border-primaryColor/50 transition-all duration-200 group-hover:shadow-lg group-hover:shadow-primaryColor/10 bg-gray-100 block"
                  >
                    {isVideo ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-900">
                        <span className="text-2xl group-hover:scale-110 transition-transform">
                          ▶️
                        </span>
                      </div>
                    ) : (
                      <img
                        src={url}
                        alt={`Product ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-xl" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-md font-bold">
                        {isVideo ? "Play" : "View"}
                      </span>
                    </div>
                  </button>

                  {/* ── Index badge ── */}
                  <span className="absolute top-1.5 left-1.5 bg-black/50 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md z-10 pointer-events-none">
                    {idx + 1}
                  </span>

                  {/* ── Download button (appears on hover) ── */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadMedia(url, idx);
                    }}
                    title="Download"
                    className="absolute bottom-1.5 right-1.5 z-10 w-6 h-6 rounded-md bg-black/60 hover:bg-primaryColor flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
                  >
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>

          {/* ── Footer: count + Download All ── */}
          <div className="flex items-center justify-between mt-3">
            <p className="text-[11px] text-gray-400">
              {allMedia.length} file{allMedia.length !== 1 ? "s" : ""} · tap to view
            </p>
            <button
              onClick={downloadAllMedia}
              className="flex items-center gap-1.5 text-[11px] font-bold text-primaryColor hover:text-primaryColor/80 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
              </svg>
              Download All
            </button>
          </div>
        </Block>
      )
      }

      {/* ── ⑦ SPECIFICATIONS ──────────────────────────────── */}
      {
        quotation.specification?.relatedQuestionsAndAnswers?.length > 0 && (
          <Block label="Specifications" icon={HiOutlineSparkles}>
            <div className="space-y-3">
              {quotation.specification.relatedQuestionsAndAnswers.map(
                (qa, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-gray-100 overflow-hidden"
                  >
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 leading-snug">
                        <span className="text-gray-400 font-bold mr-1">Q:</span>
                        {qa.question}
                      </p>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      <p className="text-sm text-gray-800">
                        <span className="text-xs font-bold text-gray-400 mr-1">
                          Yoy:
                        </span>
                        {qa.answeredByBuyer}
                      </p>
                      {qa.supplierAction && (
                        <div className="pt-2 border-t border-gray-50">
                          <p className="text-xs text-gray-500">
                            <span className="font-bold text-gray-600 mr-1">
                              Supplier:
                            </span>

                            <span
                              className={`font-bold ${qa.supplierAction.isAgreed === "Yes"
                                ? "text-emerald-600"
                                : qa.supplierAction.isAgreed === "No"
                                  ? "text-red-600"
                                  : "text-orange-500"
                                }`}
                            >
                              {qa.supplierAction.isAgreed}
                            </span>

                            {qa.supplierAction.thoughts && (
                              <span className="text-gray-400 ml-1">
                                — {qa.supplierAction.thoughts}
                              </span>
                            )}
                          </p>

                          {qa.supplierAction?.proof?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {qa.supplierAction.proof.map((img, imgIdx) => (
                                <button
                                  key={imgIdx}
                                  onClick={() => {
                                    setModalImages(qa.supplierAction.proof || []);
                                    setModalVideos([]);
                                    setMediaModalIndex(imgIdx);
                                    setMediaModalOpen(true);
                                  }}
                                  className="group"
                                >
                                  <img
                                    src={img}
                                    alt="proof"
                                    className="w-10 h-10 rounded-lg border border-gray-200 object-cover hover:opacity-90"
                                  />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ),
              )}
            </div>
          </Block>
        )
      }

      {/* ── ⑧ OTHER REQUIREMENTS ──────────────────────────── */}
      {
        quotation.specification?.otherRequirements?.length > 0 && (
          <Block label="Other Requirements" icon={MdOutlineInventory2}>
            <div className="space-y-2.5">
              {quotation.specification.otherRequirements.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-black text-gray-500 flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      {item.buyersRequirement}
                    </p>
                    <p className="text-xs mt-1.5">
                      <span className="text-gray-400">Answer: </span>
                      <span
                        className={`font-bold ${item.supplierAction?.isAgreed === "Yes"
                          ? "text-emerald-600"
                          : item.supplierAction?.isAgreed === "No"
                            ? "text-red-600"
                            : "text-amber-600"
                          }`}
                      >
                        {item.supplierAction?.isAgreed ?? "—"}
                      </span>
                    </p>
                    {item?.supplierAction?.proof?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {item.supplierAction.proof.map((img, imgIdx) => (
                          <button
                            key={imgIdx}
                            onClick={() => {
                              setModalImages(item.supplierAction.proof || []);
                              setModalVideos([]);
                              setMediaModalIndex(imgIdx);
                              setMediaModalOpen(true);
                            }}
                          >
                            <img
                              src={img}
                              alt="proof"
                              className="w-10 h-10 rounded-lg border border-gray-200 object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Block>
        )
      }

      {/* ── ⑩ DISPATCH CARDS ──────────────────────────────── */}
      {
        dispatchList.length > 0 && (
          <div className="space-y-4">
            {dispatchList.map((dispatch) => {
              const isSampleDispatch = dispatch.type === "Sample";
              const isFullOrderDispatch = dispatch.type === "Full_Order";
              const isDelivered = dispatch.status === "Delivered";
              const isDispatched = dispatch.status === "Dispatched";

              // Show Full Order Decision Banner when Delivered & Proceed_Full_Order & not approved yet
              const showFullOrderDecision =
                isFullOrderDispatch &&
                isDelivered &&
                quotation.buyerDecision === "Proceed_Full_Order" &&
                !quotation.fullOrderDetails?.approval;

              // Show Sample Approval Banner when Delivered & Pending approval
              const showSampleApproval =
                isSampleDispatch &&
                isDelivered &&
                quotation.sampleDetails?.approval === "Pending";

              // Show Proceed to Full Order button when Sample Approved & Proceed_With_Sample
              const showProceedToFullOrder =
                isSampleDispatch &&
                quotation.sampleDetails?.approval === "Approved" &&
                quotation.buyerDecision === "Proceed_With_Sample";

              return (
                <div
                  key={dispatch._id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md hover:border-primaryColor/30 transition-all duration-300 group relative"
                >
                  {/* Background Accent Glow */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primaryColor/5 via-transparent to-subPrimaryColor/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                  {/* 1. HEADER */}
                  <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100 bg-gray-50/80 flex flex-wrap items-center justify-between gap-3 relative z-10">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-primaryColor/20 to-subPrimaryColor/20 flex items-center justify-center flex-shrink-0 text-lg shadow-xs">
                        {isSampleDispatch ? "📦" : "🚚"}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 text-sm">
                          {isSampleDispatch ? "Sample Dispatch Status" : "Full Order Dispatch Status"}
                        </h4>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-0.5">
                          {dispatch.type}
                        </p>
                      </div>
                    </div>

                    {/* Status & Quick Mark Delivered Button */}
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-bold px-3 py-1 rounded-full border flex items-center gap-1.5 ${isDelivered
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : isDispatched
                            ? "bg-blue-50 text-blue-700 border-blue-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                          }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${isDelivered
                            ? "bg-emerald-500"
                            : isDispatched
                              ? "bg-blue-500 animate-pulse"
                              : "bg-amber-500"
                            }`}
                        />
                        {dispatch.status || "Ready To Dispatch"}
                      </span>

                      {isDispatched && (
                        <button
                          disabled={updating}
                          onClick={() => {
                            setSelectedDispatchId(dispatch._id);
                            setConfirmOpen(true);
                          }}
                          className="px-3 sm:px-4 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-xs font-bold hover:shadow-lg hover:scale-105 disabled:opacity-50 transition-all duration-300 flex items-center gap-1.5"
                        >
                          {updating ? (
                            <>
                              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Updating...
                            </>
                          ) : (
                            <>✓ Mark Delivered</>
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 2. BODY CONTENT */}
                  <div className="p-4 sm:p-5 space-y-4 relative z-10">
                    {/* TIMELINE */}
                    <div className="bg-gray-50/70 rounded-2xl p-4 sm:p-5 border border-gray-100">
                      <div className="relative flex justify-between items-start">
                        {getTrackingSteps(dispatch.status).map((step, idx) => (
                          <div key={idx} className="flex-1 flex flex-col items-center relative">
                            {/* Line */}
                            {idx !== 0 && (
                              <div className="absolute left-[-50%] top-3.5 sm:top-4 w-full h-1 overflow-hidden">
                                <div
                                  className={`h-full transition-all duration-700 ${step.completed ? "bg-gradient-to-r from-green-400 via-green-500 to-green-600" : "bg-gray-200"
                                    }`}
                                  style={{ animation: step.completed ? "shimmer 2s infinite" : "none" }}
                                />
                              </div>
                            )}
                            {/* Circle */}
                            <div
                              className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold z-10 transition-all duration-500 relative ${step.completed
                                ? "bg-gradient-to-br from-green-400 to-green-600 text-white scale-110 shadow-md shadow-green-500/40"
                                : "bg-gray-200 text-gray-500"
                                }`}
                            >
                              {step.completed && (
                                <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-20" />
                              )}
                              {step.completed ? "✓" : ""}
                            </div>
                            {/* Label */}
                            <p
                              className={`text-[10px] sm:text-xs mt-2 text-center leading-tight px-1 transition-all duration-500 ${step.completed ? "text-green-600 font-semibold" : "text-gray-400"
                                }`}
                            >
                              {step.label}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Timestamps */}
                      {(dispatch.dispatchedAt || dispatch.deliveredAt) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-4 pt-3 border-t border-gray-200/60">
                          {dispatch.dispatchedAt && (
                            <div className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                              <span className="text-xs text-gray-400 flex-shrink-0">Dispatched:</span>
                              <span className="text-xs font-semibold text-gray-700 truncate">
                                {new Date(dispatch.dispatchedAt).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {dispatch.deliveredAt && (
                            <div className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                              <span className="text-xs text-gray-400 flex-shrink-0">Delivered:</span>
                              <span className="text-xs font-semibold text-gray-700 truncate">
                                {new Date(dispatch.deliveredAt).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* AWB & Tracking Section */}
                    {(dispatch.AWB_Number || dispatch.trackingUrl) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {dispatch.AWB_Number && (
                          <div className="flex items-center justify-between gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                            <div className="flex items-center gap-2">
                              <span className="text-blue-600 text-base">📦</span>
                              <span className="text-xs font-semibold text-blue-700">AWB Number</span>
                            </div>
                            <span className="text-xs font-mono font-bold text-blue-900 bg-white px-3 py-1.5 rounded-lg border border-blue-200 shadow-2xs">
                              {dispatch.AWB_Number}
                            </span>
                          </div>
                        )}

                        {dispatch.trackingUrl && (
                          <div className="flex items-center justify-between gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-600 text-base">🔗</span>
                              <span className="text-xs font-semibold text-emerald-700">Tracking Link</span>
                            </div>
                            <a
                              href={dispatch.trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-bold text-emerald-700 bg-white hover:bg-emerald-600 hover:text-white px-3 py-1.5 rounded-lg border border-emerald-200 transition-all duration-200 flex items-center gap-1.5 shadow-2xs"
                            >
                              Open Tracking
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 3. FULL ORDER DECISION & ACTIONS (Delivered) */}
                    {showFullOrderDecision && (
                      <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                        <CountdownTimer
                          timeRemaining={timeRemaining}
                          isExpired={timeRemaining.expired}
                        />

                        {!timeRemaining.expired && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                            {/* SATISFIED */}
                            <button
                              disabled={updating}
                              onClick={() => {
                                setFullOrderAction("SATISFIED");
                                setFullOrderConfirmOpen(true);
                              }}
                              className="w-full py-2.5 px-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl text-xs sm:text-sm font-bold hover:shadow-lg hover:scale-[1.01] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            >
                              <span className="text-base">✓</span> Satisfied
                            </button>
                            {/* DISPUTE */}
                            <button
                              disabled={updating}
                              onClick={() => setReturnDisputeModalOpen(true)}
                              className="w-full py-2.5 px-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl text-xs sm:text-sm font-bold hover:shadow-lg hover:scale-[1.01] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            >
                              <span className="text-base">⚠</span> Raise Dispute
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 4. SAMPLE APPROVAL ACTIONS (Sample Delivered) */}
                    {showSampleApproval && (
                      <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-center gap-2.5">
                          <span className="text-lg">📦</span>
                          <div>
                            <p className="text-xs font-bold text-amber-800">Sample Delivered</p>
                            <p className="text-[11px] text-amber-700">Please inspect the sample and submit your approval decision.</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <button
                            disabled={updating}
                            onClick={() => {
                              setApprovalAction("Approved");
                              setApprovalConfirmOpen(true);
                            }}
                            className="w-full py-2.5 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl text-xs sm:text-sm font-bold hover:shadow-md transition flex items-center justify-center gap-1.5"
                          >
                            ✓ Approve the Sample
                          </button>
                          <button
                            disabled={updating}
                            onClick={() => {
                              setApprovalAction("Rejected");
                              setApprovalConfirmOpen(true);
                            }}
                            className="w-full py-2.5 px-4 bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs sm:text-sm font-bold hover:shadow-md transition flex items-center justify-center gap-1.5"
                          >
                            ✕ Reject the Sample
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 5. PROCEED TO FULL ORDER (Sample Approved) */}
                    {showProceedToFullOrder && (
                      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3.5 bg-gradient-to-r from-primaryColor/5 to-subPrimaryColor/5 rounded-xl border border-primaryColor/20">
                        <div>
                          <p className="text-xs font-bold text-gray-900">Sample Approved Successfully 🎉</p>
                          <p className="text-[11px] text-gray-500">You can now proceed to request transportation charges and confirm the full order.</p>
                        </div>
                        <button
                          disabled={updating}
                          onClick={() => {
                            setBuyerDecision("Proceed_Full_Order");
                            setDecisionConfirmOpen(true);
                          }}
                          className="px-4 py-2 bg-gradient-to-r from-primaryColor to-subPrimaryColor text-white rounded-xl text-xs font-bold hover:shadow-md hover:scale-[1.01] transition flex items-center justify-center gap-1.5 flex-shrink-0"
                        >
                          {updating ? "Processing..." : "🚀 Proceed To Full Order"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      }

      {/* ── CONFIRM MODALS (all preserved) ───────────────── */}
      <ConfirmActionModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelivered}
        loading={updating}
        title="Mark as Delivered"
        description="Are you sure you want to mark this dispatch as delivered?"
        confirmText="Yes, Mark Delivered"
      />
      <SampleApprovalModal
        open={approvalConfirmOpen}
        onClose={() => {
          setApprovalConfirmOpen(false);
          setDeclarationChecked(false);
        }}
        onConfirm={handleSampleApproval}
        action={approvalAction}
        loading={updating}
        declarationChecked={declarationChecked}
        setDeclarationChecked={setDeclarationChecked}
      />

      <ConfirmActionModal
        open={decisionConfirmOpen}
        onClose={() => setDecisionConfirmOpen(false)}
        onConfirm={handleBuyerDecision}
        loading={updating}
        title={
          buyerDecision === "Proceed_Full_Order"
            ? "Proceed to Full Order"
            : "Close Order"
        }
        description={
          buyerDecision === "Proceed_Full_Order"
            ? "Are you sure you want to proceed with full order?"
            : "Are you sure you want to close this order?"
        }
        confirmText={
          buyerDecision === "Proceed_Full_Order" ? "Yes, Proceed" : "Yes, Close"
        }
      />


      <ConfirmActionModal
        open={fullOrderConfirmOpen}
        onClose={() => setFullOrderConfirmOpen(false)}
        onConfirm={handleFullOrderAction}
        loading={updating}
        title={
          fullOrderAction === "DISPUTE" ? "Raise Dispute" : "Mark as Satisfied"
        }
        description={
          fullOrderAction === "DISPUTE"
            ? "Are you sure you want to raise a dispute?"
            : "Are you sure you are satisfied with this order?"
        }
        confirmText={
          fullOrderAction === "DISPUTE" ? "Yes, Raise" : "Yes, Confirm"
        }
      />

      <RaiseReturnDisputeModal
        open={returnDisputeModalOpen}
        onClose={() => setReturnDisputeModalOpen(false)}
        onConfirm={handleRaiseReturnDispute}
        loading={updating}
        quotationUniqueNumber={quotation.quotationUniqueNumber}
      />

      {/* ── Image / Video Modal ── */}
      {
        mediaModalOpen && (
          <ImageModal
            images={modalImages}
            videos={modalVideos}
            initialIndex={mediaModalIndex}
            onClose={() => setMediaModalOpen(false)}
          />
        )
      }

      {/* ── NEW: Proforma Invoice Modal ─────────────────────────────────────── */}
      <ProformaInvoiceModal
        open={proformaModalOpen}
        onClose={() => setProformaModalOpen(false)}
        quotation={{
          ...quotation,
          priceBreakdown,
        }}
        order={quotation.orderId || {}}
        isSampleReq={activeBreakdownTab === "sample"}
        user={user}
        bankTransferDocument={bankTransferDocument}
        onSuccess={async () => {
          setProformaModalOpen(false);
          toast.success(
            activeBreakdownTab === "sample"
              ? "Sample order confirmed! 🎉"
              : "Order placed successfully! 🎉",
          );
          await refreshQuotations?.();
          await refreshOrders?.();
        }}
      />
    </div >
  );
};

export default SelectedQuotationView;